import {
  ApplicationConfig,
  inject,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient } from '@angular/common/http';
import { provideRouter, Router } from '@angular/router';
import { provideAuth, secureRoutesFor, withAuthInterceptor } from '@presencelearning/auth';
import { datadogRum } from '@datadog/browser-rum';
import { initLogging } from '@presencelearning/logging';
import {
  angularPlugin,
  provideDatadogErrorHandler,
  provideDatadogRouter,
} from '@datadog/browser-rum-angular';
import {
  DEFAULT_COMMAND_PALETTE_PLACEHOLDER,
  DEFAULT_COMMAND_PALETTE_SHORTCUT,
  PL_COMMAND_PALETTE_CONFIG,
} from '@presencelearning/ui/command-palette';
import { providePlIcons } from '@presencelearning/icons';
import { detectBrowserLocale, providePlI18n } from '@presencelearning/i18n';
import { TranslocoService } from '@jsverse/transloco';
import { provideRemoteConfig, withConfigCat } from '@presencelearning/remote-config';
import { routes } from './app.routes';
import { logsInitConfiguration, rumInitConfiguration } from './observability';
import { createPagesSearchSource } from './pages-search-source';
import { environment } from '../environments/environment';
import en from '../assets/i18n/en.json';
import es from '../assets/i18n/es.json';

const AVAILABLE_LANGS = ['en', 'es'];

// Guarded so this is a genuine no-op (not even the SDKs' internal state gets
// touched) in local dev, where environment.ts intentionally leaves
// datadogApplicationId/datadogClientToken empty - staging and production
// have real values (see environment.staging.ts / environment.production.ts).
function initializeDatadog(): void {
  const logs = logsInitConfiguration(environment.datadogClientToken, environment.env);
  const rum = rumInitConfiguration(
    environment.datadogApplicationId,
    environment.datadogClientToken,
    environment.env
  );
  if (!logs || !rum) {
    return;
  }

  datadogRum.init({
    ...rum,
    plugins: [angularPlugin({ router: true })],
  });
  initLogging(logs);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideDatadogRouter(),
    provideDatadogErrorHandler(),
    provideAnimations(),
    provideHttpClient(withAuthInterceptor()),
    // 0.9 is deny-by-default: an omitted list attaches the token to nothing.
    provideAuth({
      issuerUrl: environment.auth.issuerUrl,
      clientId: environment.auth.clientId,
      secureRoutes: secureRoutesFor(environment.apps),
    }),
    providePlIcons(),
    providePlI18n({
      availableLangs: AVAILABLE_LANGS,
      defaultLang: detectBrowserLocale(AVAILABLE_LANGS, 'en'),
      translations: { en, es },
    }),
    provideRemoteConfig(withConfigCat({ sdkKey: environment.configCatKey })),
    // Runs only when Angular actually bootstraps the application, not merely
    // when this module is imported (e.g. by a future spec or Storybook config
    // that imports appConfig without booting the real app).
    provideEnvironmentInitializer(initializeDatadog),
    // provideCommandPalette() takes sources as a plain array, resolved before
    // any injector exists - it can't inject Router itself. Providing the
    // token directly via useFactory (mirroring @presencelearning/ai's
    // PRESENCE_AI_CONFIG pattern) runs inside an injection context instead.
    {
      provide: PL_COMMAND_PALETTE_CONFIG,
      useFactory: () => ({
        sources: [createPagesSearchSource(inject(Router), inject(TranslocoService))],
        debounceMs: 300,
        minSearchLength: 2,
        shortcut: DEFAULT_COMMAND_PALETTE_SHORTCUT,
        placeholder: DEFAULT_COMMAND_PALETTE_PLACEHOLDER,
      }),
    },
  ],
};
